import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import {
    ArrowLeft,
    Mail,
    MessageSquare,
    MapPin,
    Clock,
    Send,
    Loader2,
    CheckCircle,
    Target,
} from 'lucide-react';

export default function ContactPage() {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        // Simulate form submission
        await new Promise(resolve => setTimeout(resolve, 1500));
        setIsSubmitting(false);
        setIsSubmitted(true);
    };

    return (
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex flex-col">
            {/* Header */}
            <header className="bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/" className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg">
                            <ArrowLeft className="w-5 h-5" />
                        </Link>
                        <div className="flex items-center gap-2">
                            <Mail className="w-5 h-5 text-primary-600" />
                            <h1 className="text-xl font-bold">{t('contact.title', 'Liên hệ')}</h1>
                        </div>
                    </div>
                    <Link to="/login" className="btn-primary text-sm">
                        {t('auth.login')}
                    </Link>
                </div>
            </header>

            {/* Content */}
            <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
                {/* Hero */}
                <section className="text-center py-8">
                    <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                        {t('contact.hero.title', 'Chúng tôi luôn sẵn sàng hỗ trợ bạn')}
                    </h2>
                    <p className="text-neutral-600 dark:text-neutral-400 max-w-xl mx-auto">
                        {t('contact.hero.subtitle', 'Có câu hỏi hoặc phản hồi? Liên hệ với đội ngũ VisionBoard qua các kênh dưới đây.')}
                    </p>
                </section>

                <div className="grid md:grid-cols-2 gap-8">
                    {/* Contact Info */}
                    <div className="space-y-6">
                        <div className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-700">
                            <h3 className="text-lg font-semibold mb-6">
                                {t('contact.info.title', 'Thông tin liên hệ')}
                            </h3>

                            <div className="space-y-4">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 bg-primary-100 dark:bg-primary-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <Mail className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium">{t('contact.info.email', 'Email')}</p>
                                        <a href="mailto:support@1vision.work" className="text-primary-600 hover:underline">
                                            support@1vision.work
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 bg-success-100 dark:bg-success-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <MessageSquare className="w-5 h-5 text-success-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium">{t('contact.info.chat', 'Live Chat')}</p>
                                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                            {t('contact.info.chatDesc', 'Nhấn nút chat ở góc phải màn hình')}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 bg-warning-100 dark:bg-warning-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <Clock className="w-5 h-5 text-warning-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium">{t('contact.info.hours', 'Giờ làm việc')}</p>
                                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                            {t('contact.info.hoursDesc', 'Thứ 2 - Thứ 6: 9:00 - 18:00 (GMT+7)')}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 bg-secondary-100 dark:bg-secondary-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <MapPin className="w-5 h-5 text-secondary-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium">{t('contact.info.address', 'Địa chỉ')}</p>
                                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                            Hà Nội, Việt Nam
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Response Time */}
                        <div className="bg-gradient-to-br from-primary-600 to-secondary-600 rounded-2xl p-6 text-white">
                            <h3 className="font-semibold mb-2">
                                {t('contact.response.title', 'Thời gian phản hồi')}
                            </h3>
                            <p className="text-sm text-white/80">
                                {t('contact.response.desc', 'Chúng tôi thường phản hồi trong vòng 24 giờ làm việc. Các yêu cầu khẩn cấp sẽ được ưu tiên xử lý.')}
                            </p>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-700">
                        {isSubmitted ? (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 bg-success-100 dark:bg-success-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="w-8 h-8 text-success-600" />
                                </div>
                                <h3 className="text-xl font-semibold mb-2">
                                    {t('contact.form.success', 'Gửi thành công!')}
                                </h3>
                                <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                                    {t('contact.form.successDesc', 'Chúng tôi sẽ liên hệ lại với bạn sớm nhất có thể.')}
                                </p>
                                <button
                                    onClick={() => {
                                        setIsSubmitted(false);
                                        setFormData({ name: '', email: '', subject: '', message: '' });
                                    }}
                                    className="btn-primary"
                                >
                                    {t('contact.form.sendAnother', 'Gửi tin nhắn khác')}
                                </button>
                            </div>
                        ) : (
                            <>
                                <h3 className="text-lg font-semibold mb-6">
                                    {t('contact.form.title', 'Gửi tin nhắn')}
                                </h3>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="label">{t('contact.form.name', 'Họ và tên')}</label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="input"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="label">{t('auth.email', 'Email')}</label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className="input"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="label">{t('contact.form.subject', 'Chủ đề')}</label>
                                        <select
                                            value={formData.subject}
                                            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                            className="input"
                                            required
                                        >
                                            <option value="">{t('contact.form.selectSubject', 'Chọn chủ đề')}</option>
                                            <option value="general">{t('contact.form.subjects.general', 'Câu hỏi chung')}</option>
                                            <option value="support">{t('contact.form.subjects.support', 'Hỗ trợ kỹ thuật')}</option>
                                            <option value="billing">{t('contact.form.subjects.billing', 'Thanh toán & Tài khoản')}</option>
                                            <option value="feedback">{t('contact.form.subjects.feedback', 'Góp ý & Đề xuất')}</option>
                                            <option value="partnership">{t('contact.form.subjects.partnership', 'Hợp tác')}</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="label">{t('contact.form.message', 'Nội dung')}</label>
                                        <textarea
                                            value={formData.message}
                                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                            className="input min-h-[120px]"
                                            required
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="btn-primary w-full"
                                    >
                                        {isSubmitting ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <>
                                                <Send className="w-4 h-4" />
                                                {t('contact.form.submit', 'Gửi tin nhắn')}
                                            </>
                                        )}
                                    </button>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="py-8 px-4 bg-neutral-100 dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700 mt-auto">
                <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                            <Target className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-neutral-900 dark:text-white">VisionBoard</span>
                    </Link>
                    <div className="flex items-center gap-4 text-sm text-neutral-500">
                        <Link to="/terms" className="hover:text-primary-600">Điều khoản</Link>
                        <Link to="/privacy" className="hover:text-primary-600">Bảo mật</Link>
                        <Link to="/help" className="hover:text-primary-600">Hỗ trợ</Link>
                    </div>
                    <p className="text-sm text-neutral-500">© 2025 VisionBoard</p>
                </div>
            </footer>
        </div>
    );
}
